/**
 * Build a recipe out of ingredients.
 *
 * This is the screen the module was missing. The library used to be a flat list where
 * "Tomato, 100 g" sat beside "Dinner" as if they were the same kind of thing, so planning a
 * week meant planning tomatoes. A recipe here is COMPOSED: pick ingredients from the
 * library, say how many portions of each, and the macros add themselves up.
 *
 * THE LIVE TOTAL IS THE REAL ARITHMETIC. It imports src/shared/macros.ts — the same function
 * the Worker runs a second later — rather than a lookalike in the SPA. A second copy of the
 * rounding, and of the rule about carrying `partial`, is exactly how the two would drift
 * until the number you approved was not the number that got stored.
 *
 * A MISSING MACRO IS NEVER ZERO, at any level. If one ingredient has no sodium recorded, the
 * recipe's sodium is the sum of the ones that do AND is flagged as short — stated in words,
 * here, before saving, because this is the last moment anyone can do something about it.
 */
import { useMemo, useRef, useState } from 'react';
import { api, ApiRequestError } from '../../lib/api';
import type {
  WellnessIngredient,
  WellnessMacroKey,
  WellnessRecipe,
} from '../../shared/types';
import { MACRO_KEYS, rollUpIngredients } from '../../shared/macros';
import { macro, macroLabel } from './format';
import IngredientEditor from './IngredientEditor';
import RecipePhoto from './RecipePhoto';
import Sheet from './Sheet';

/** Formats a browser will actually paint. Keep in step with worker/routes/wellness.ts. */
const PHOTO_ACCEPT = 'image/jpeg,image/png,image/webp,image/gif';
const PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const HEIC_TYPES = ['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence'];
const HEIC_HELP =
  'iPhone HEIC photos don’t show up in web browsers. On your iPhone: Settings → Camera → Formats → “Most Compatible”, then take the photo again — or open the photo, tap Edit, and save a copy as a JPEG.';
const MAX_PHOTO_BYTES = 8 * 1024 * 1024;

const MACRO_UNITS: Record<WellnessMacroKey, string> = {
  calories: 'kcal',
  protein_g: 'g',
  fat_g: 'g',
  carb_g: 'g',
  sodium_mg: 'mg',
  potassium_mg: 'mg',
};

const inputCls =
  'min-h-11 w-full rounded-xl border border-border bg-canvas px-3 text-ink placeholder:text-ink-faint focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage';
const labelCls = 'mb-1 block text-sm font-medium text-ink';

/** A line being edited. `quantity` stays a string so a half-typed "0." isn't clobbered. */
interface DraftLine {
  ingredient: WellnessIngredient;
  quantity: string;
}

export default function RecipeEditor({
  recipe,
  ingredients,
  onClose,
  onSaved,
  onIngredientsChanged,
}: {
  recipe: WellnessRecipe | null;
  ingredients: WellnessIngredient[];
  onClose: () => void;
  onSaved: () => void;
  /** The library owns the ingredient list; a new one added from in here has to reach it. */
  onIngredientsChanged: () => void;
}) {
  const [name, setName] = useState(recipe?.name ?? '');
  const [description, setDescription] = useState(recipe?.description ?? '');
  const [instructions, setInstructions] = useState(recipe?.instructions ?? '');
  const [servings, setServings] = useState(String(recipe?.servings ?? 1));
  const [prepMin, setPrepMin] = useState(recipe?.prep_min === null || recipe?.prep_min === undefined ? '' : String(recipe.prep_min));
  const [mode, setMode] = useState(recipe?.macro_mode ?? 'rolled');
  const [lines, setLines] = useState<DraftLine[]>(
    () =>
      recipe?.ingredients.map((l) => ({ ingredient: l.ingredient, quantity: String(l.quantity) })) ??
      []
  );
  const [stated, setStated] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      MACRO_KEYS.map((k) => {
        const v = recipe?.[k];
        return [k, v === null || v === undefined ? '' : String(v)];
      })
    )
  );

  const [picking, setPicking] = useState(false);
  const [newIngredient, setNewIngredient] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Photo. A new recipe has no id to upload against yet, so the file is held and sent the
  // moment the row exists — same as the family meal editor.
  const fileInput = useRef<HTMLInputElement>(null);
  const [pendingPhoto, setPendingPhoto] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(recipe?.photo_url ?? null);

  /**
   * The live per-serving rollup, on the same code path as the server's.
   * A blank quantity counts as 1 rather than as nothing, matching the Worker.
   */
  const rolled = useMemo(
    () =>
      rollUpIngredients(
        lines.map((l) => ({
          quantity: Number(l.quantity) > 0 ? Number(l.quantity) : 1,
          macros: {
            calories: l.ingredient.calories,
            protein_g: l.ingredient.protein_g,
            fat_g: l.ingredient.fat_g,
            carb_g: l.ingredient.carb_g,
            sodium_mg: l.ingredient.sodium_mg,
            potassium_mg: l.ingredient.potassium_mg,
          },
        })),
        Number(servings) > 0 ? Number(servings) : 1
      ),
    [lines, servings]
  );

  const choosePhoto = (file: File | undefined) => {
    if (!file) return;
    setError(null);
    const type = file.type.toLowerCase();
    if (HEIC_TYPES.includes(type)) return setError(HEIC_HELP);
    if (!PHOTO_TYPES.includes(type)) {
      return setError('That file is not a picture this app can show. Use a JPEG, PNG, WEBP or GIF.');
    }
    if (file.size > MAX_PHOTO_BYTES) return setError('Photos need to be under 8MB.');
    if (file.size === 0) return setError('That file is empty.');

    if (preview) URL.revokeObjectURL(preview);
    setPreview(URL.createObjectURL(file));
    setPendingPhoto(file);
  };

  const removePhoto = async () => {
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null);
    setPendingPhoto(null);
    if (recipe?.photo_url) {
      const updated = await api.del<WellnessRecipe>(`/wellness/recipes/${recipe.id}/photo`);
      setPhotoUrl(updated.photo_url);
      onSaved();
    }
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const body = {
        name,
        description: description.trim() || null,
        instructions: instructions.trim() || null,
        servings: servings.trim() || 1,
        prep_min: prepMin.trim() === '' ? null : prepMin,
        macro_mode: mode,
        ingredients: lines.map((l) => ({
          ingredient_id: l.ingredient.id,
          quantity: l.quantity.trim() === '' ? 1 : Number(l.quantity),
        })),
        // Only read by the server in `stated` mode; '' becomes null there, never 0.
        ...Object.fromEntries(MACRO_KEYS.map((k) => [k, stated[k] ?? ''])),
      };

      const saved = recipe
        ? await api.put<WellnessRecipe>(`/wellness/recipes/${recipe.id}`, body)
        : await api.post<WellnessRecipe>('/wellness/recipes', body);

      if (pendingPhoto) {
        const form = new FormData();
        form.append('photo', pendingPhoto);
        await api.upload<WellnessRecipe>(`/wellness/recipes/${saved.id}/photo`, form);
      }
      onSaved();
    } catch (err) {
      setError(
        err instanceof ApiRequestError && err.body.detail
          ? err.body.detail
          : 'That did not save. Try again?'
      );
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!recipe) return;
    setSaving(true);
    try {
      await api.del(`/wellness/recipes/${recipe.id}`);
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  const shownPhoto = preview ?? photoUrl;
  const chosen = new Set(lines.map((l) => l.ingredient.id));

  return (
    <Sheet
      title={recipe ? 'Edit recipe' : 'New recipe'}
      onClose={onClose}
      footer={
        <button
          type="submit"
          form="recipe-form"
          disabled={saving}
          className="min-h-12 w-full rounded-full bg-sage px-4 font-medium text-white disabled:opacity-60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
        >
          {saving ? 'Saving…' : recipe ? 'Save recipe' : 'Add to the library'}
        </button>
      }
    >
      <form id="recipe-form" className="space-y-5" onSubmit={save}>
        {error && (
          <p role="alert" className="rounded-xl bg-clay-soft px-3 py-2 text-sm text-clay">
            {error}
          </p>
        )}

        {/* photo */}
        <div className="flex items-center gap-4">
          <RecipePhoto name={name || 'Recipe'} url={shownPhoto} className="size-20 shrink-0 rounded-2xl" />
          <div>
            <span className={labelCls}>Photo</span>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => fileInput.current?.click()}
                className="min-h-11 rounded-full border border-border bg-surface px-4 text-sm font-medium text-ink-soft focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
              >
                {shownPhoto ? 'Replace' : 'Add photo'}
              </button>
              {shownPhoto && (
                <button
                  type="button"
                  onClick={() => void removePhoto()}
                  className="min-h-11 rounded-full px-3 text-sm text-ink-soft hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
                >
                  Remove
                </button>
              )}
            </div>
            {pendingPhoto && (
              <p className="mt-1 text-xs text-ink-faint">{pendingPhoto.name} — uploads when you save.</p>
            )}
          </div>
          <input
            ref={fileInput}
            type="file"
            accept={PHOTO_ACCEPT}
            className="sr-only"
            aria-label="Recipe photo"
            onChange={(e) => choosePhoto(e.target.files?.[0])}
          />
        </div>

        {/* name / servings / time */}
        <label className="block">
          <span className={labelCls}>Name</span>
          <input
            autoFocus
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Skillet sirloin bowl"
            className={inputCls}
          />
        </label>

        <div className="flex gap-3">
          <label className="w-28 block">
            <span className={labelCls}>Servings</span>
            <input
              value={servings}
              onChange={(e) => setServings(e.target.value)}
              inputMode="numeric"
              placeholder="1"
              className={inputCls}
            />
          </label>
          <label className="w-28 block">
            <span className={labelCls}>Minutes</span>
            <input
              value={prepMin}
              onChange={(e) => setPrepMin(e.target.value)}
              inputMode="numeric"
              placeholder="20"
              className={inputCls}
            />
          </label>
        </div>
        {Number(servings) > 1 && (
          <p className="-mt-3 text-xs text-ink-soft">
            The ingredients below are for the whole thing. Macros are shown per serving, which
            is what planning it into one slot means.
          </p>
        )}

        {/* ingredients */}
        <div>
          <div className="mb-2 flex items-center justify-between gap-3">
            <span className={`${labelCls} mb-0`}>Ingredients</span>
            <button
              type="button"
              onClick={() => setPicking(true)}
              className="min-h-9 rounded-full border border-sage bg-surface px-3 text-sm font-medium text-sage focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
            >
              + Add
            </button>
          </div>

          {lines.length === 0 ? (
            <p className="rounded-xl border border-dashed border-border px-3 py-4 text-center text-sm text-ink-soft">
              No ingredients yet. Add a few and the macros add themselves up.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {lines.map((line, i) => (
                <li
                  key={line.ingredient.id}
                  className="flex items-center gap-2 rounded-xl border border-border bg-canvas px-3 py-2"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-ink">{line.ingredient.name}</span>
                    <span className="block text-xs text-ink-soft">
                      per {line.ingredient.portion_desc} · {macro(line.ingredient.calories)} kcal ·{' '}
                      {macro(line.ingredient.sodium_mg, 'mg')} sodium
                    </span>
                  </span>
                  <label className="shrink-0">
                    <span className="sr-only">Portions of {line.ingredient.name}</span>
                    <input
                      value={line.quantity}
                      onChange={(e) =>
                        setLines((prev) =>
                          prev.map((l, j) => (j === i ? { ...l, quantity: e.target.value } : l))
                        )
                      }
                      inputMode="decimal"
                      placeholder="1"
                      className="min-h-11 w-16 rounded-xl border border-border bg-surface px-2 text-center text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
                    />
                  </label>
                  <button
                    type="button"
                    onClick={() => setLines((prev) => prev.filter((_, j) => j !== i))}
                    aria-label={`Remove ${line.ingredient.name}`}
                    className="inline-flex h-11 w-9 shrink-0 items-center justify-center rounded-full text-ink-faint hover:text-clay focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}
          {lines.length > 0 && (
            <p className="mt-1.5 text-xs text-ink-faint">
              The number is how many portions — 4 eggs against a “1 large” portion is 4, half an
              avocado is 0.5.
            </p>
          )}
        </div>

        {/* macros */}
        <div className="rounded-2xl border border-border bg-canvas p-3">
          <div className="mb-2 flex flex-wrap gap-1">
            {(
              [
                ['rolled', 'Add up the ingredients'],
                ['stated', 'I’ll type the numbers'],
              ] as const
            ).map(([m, label]) => (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                aria-pressed={mode === m}
                className={`min-h-9 rounded-full px-3 text-xs font-medium focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage ${
                  mode === m ? 'bg-ink text-canvas' : 'border border-border bg-surface text-ink-soft'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {mode === 'rolled' ? (
            <>
              <dl className="grid grid-cols-3 gap-2">
                {MACRO_KEYS.map((k) => (
                  <div key={k} className="rounded-xl bg-surface px-2 py-1.5">
                    <dt className="text-xs text-ink-soft">{macroLabel(k)}</dt>
                    <dd className="text-sm font-medium text-ink">
                      {macro(rolled[k])}
                      <span className="text-xs font-normal text-ink-faint"> {MACRO_UNITS[k]}</span>
                      {/* An asterisk with a footnote, not a colour. A colour-only "this is
                          understated" is invisible to anyone who cannot see it, and this is
                          the number the salt work turns on. */}
                      {rolled.partial.includes(k) && (
                        <span className="font-semibold text-honey">
                          *<span className="sr-only"> (short — see the note below)</span>
                        </span>
                      )}
                    </dd>
                  </div>
                ))}
              </dl>
              {rolled.partial.length > 0 && (
                <p className="mt-2 rounded-xl bg-honey-soft px-3 py-2 text-xs text-honey">
                  * {rolled.partial.map(macroLabel).join(', ')}{' '}
                  {rolled.partial.length === 1 ? 'is' : 'are'} short — an ingredient here has no
                  value recorded for {rolled.partial.length === 1 ? 'it' : 'them'}. The total
                  stays what it is rather than counting the gap as zero, and every day this lands
                  on will say so. Open the ingredient and press Look up to close it.
                </p>
              )}
              {lines.length === 0 && (
                <p className="mt-2 text-xs text-ink-soft">
                  Nothing to add up yet. Add ingredients, or switch to typing the numbers.
                </p>
              )}
            </>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                {MACRO_KEYS.map((k) => (
                  <label key={k} className="block">
                    <span className={labelCls}>
                      {macroLabel(k)} ({MACRO_UNITS[k]})
                    </span>
                    <input
                      inputMode="numeric"
                      value={stated[k] ?? ''}
                      onChange={(e) => setStated((p) => ({ ...p, [k]: e.target.value }))}
                      className={inputCls}
                    />
                  </label>
                ))}
              </div>
              <p className="mt-2 text-xs text-ink-soft">
                These numbers win over the ingredient list — which is right when they came from
                somewhere better, like your protocol doc. Leave anything you don’t know blank; it
                stays blank rather than counting as zero.
              </p>
            </>
          )}
        </div>

        {/* words */}
        <label className="block">
          <span className={labelCls}>What it is</span>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Sirloin seared, avocado on top, spinach wilted in the pan"
            className={inputCls}
          />
        </label>

        <label className="block">
          <span className={labelCls}>How to make it</span>
          <textarea
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            rows={4}
            placeholder={'Sear the sirloin 3 minutes a side.\nWilt the spinach in the same pan.'}
            className="w-full rounded-xl border border-border bg-canvas px-3 py-2 text-ink placeholder:text-ink-faint focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
          />
        </label>

        {recipe && (
          <button
            type="button"
            onClick={remove}
            disabled={saving}
            className="min-h-11 w-full rounded-full border border-border bg-surface px-4 text-sm font-medium text-ink-soft focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
          >
            Remove from the library
          </button>
        )}
      </form>

      {picking && (
        <IngredientPicker
          ingredients={ingredients.filter((i) => !chosen.has(i.id))}
          onClose={() => setPicking(false)}
          onNew={() => {
            setPicking(false);
            setNewIngredient(true);
          }}
          onPick={(ing) => {
            setLines((prev) => [...prev, { ingredient: ing, quantity: '1' }]);
            setPicking(false);
          }}
        />
      )}

      {newIngredient && (
        <IngredientEditor
          ingredient={null}
          onClose={() => setNewIngredient(false)}
          onSaved={(saved) => {
            // Straight onto the recipe — the reason you opened it was to use it.
            setLines((prev) => [...prev, { ingredient: saved, quantity: '1' }]);
            setNewIngredient(false);
            onIngredientsChanged();
          }}
        />
      )}
    </Sheet>
  );
}

function IngredientPicker({
  ingredients,
  onPick,
  onNew,
  onClose,
}: {
  ingredients: WellnessIngredient[];
  onPick: (i: WellnessIngredient) => void;
  onNew: () => void;
  onClose: () => void;
}) {
  const [q, setQ] = useState('');
  const shown = ingredients.filter((i) =>
    `${i.name} ${i.portion_desc}`.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <Sheet title="Add an ingredient" onClose={onClose}>
      <input
        autoFocus
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search your ingredients"
        className={`mb-3 ${inputCls}`}
      />
      <button
        type="button"
        onClick={onNew}
        className="mb-3 min-h-11 w-full rounded-full border border-sage bg-surface px-4 text-sm font-medium text-sage focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
      >
        New ingredient — look it up or type it
      </button>
      {shown.length === 0 ? (
        <p className="px-1 py-6 text-center text-sm text-ink-soft">
          {ingredients.length === 0
            ? 'Everything in your library is already on this recipe.'
            : 'Nothing matches that. Add it as a new ingredient.'}
        </p>
      ) : (
        <ul className="space-y-1.5">
          {shown.map((i) => (
            <li key={i.id}>
              <button
                type="button"
                onClick={() => onPick(i)}
                className="w-full rounded-xl border border-border bg-canvas px-3 py-2.5 text-left hover:border-sage focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
              >
                <span className="block font-medium text-ink">
                  {i.name}
                  <span className="font-normal text-ink-soft"> · {i.portion_desc}</span>
                </span>
                <span className="block text-xs text-ink-soft">
                  {macro(i.calories)} kcal · {macro(i.protein_g, 'g')} protein ·{' '}
                  {macro(i.sodium_mg, 'mg')} sodium
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </Sheet>
  );
}
